import { Button } from "@mui/joy";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRecoilState, useRecoilValue } from "recoil";
import { userAtom } from "recoil/userAtom";
import { sendMessage } from "utils/webview";
import useIsInappWebview from "hooks/useIsInappWebview";
import useGetEventFromApp from "hooks/useGetEventFromApp";
import { useCallback, useEffect, useState } from "react";
import { USER_DETAIL_ENDPOINT, USER_DOMAIN, userApi } from "api/userApi";
import ButtonWithModal from "components/common/ButtonWithModal";
import { paymentOrderAtom } from "recoil/paymentOrderAtom";
import { PATH } from "routes/paths";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../../../../api";
import { useConfirmStoreReviewerPayment } from "hooks/useConfirmStoreReviewerPayment";
import { compareVersions } from "logic/logics_common";
import useGetDeviceInfo from "hooks/useGetDeviceInfo";
import useMixpanelWeb from "hooks/useMixpanelWeb";
import { appsflyerApi } from "../../../../api/appsflyerApi";
import { PlanKey } from "../PaymentContent100DayABCPlan";
import ReactPixel from "react-facebook-pixel";

export default function PaymentButtonWithPlanOption({
  selectedPlan,
}: {
  selectedPlan: PlanKey;
}) {
  const isInappWebview = useIsInappWebview();

  const navigate = useNavigate();
  const [user, setUser] = useRecoilState(userAtom);
  const paymentOrderState = useRecoilValue(paymentOrderAtom);
  const parsedMessageFromApp = useGetEventFromApp();
  const queryClient = useQueryClient();

  const { appVersion } = useGetDeviceInfo();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDone, setIsDone] = useState<boolean>(false); //중복 방지
  const [disabled, setDisabled] = useState<boolean>(false);

  const setMixPanel = useMixpanelWeb({
    eventName: "결제 버튼 클릭",
    params: {
      type: `${paymentOrderState?.paymentProvider}`,
      isFirst: `${paymentOrderState?.isFirst}`,
    },
  });

  const {
    mutate: confirmStoreReviewerPayments,
    isLoading: isLoadingConifirmReviewer,
    isSuccess,
    isError,
  } = useConfirmStoreReviewerPayment();

  useQuery(
    [USER_DOMAIN, USER_DETAIL_ENDPOINT],
    useCallback(() => userApi.getDetail(), []),
    {
      enabled:
        !!axiosInstance.defaults.headers["X-Auth-Token"] && !!user?.accessToken,

      onSuccess: (data) => {
        if (data && data.result) {
          //결제 탭 닫기
          if (data.result.subscription?.isSubscribing) {
            const expiryDaysLeft = data.result.subscription?.expiryDaysLeft;
            if (expiryDaysLeft === undefined || expiryDaysLeft > 25) {
              // 넉넉잡아 25일 (원래 만료 20일 전부터 페이지 진입 가능)
              setDisabled(true);
            }

            if (!user?.subscription?.isSubscribing) {
              if (isInappWebview) {
                // 닫기 토스페이먼츠 창 닫기
                sendMessage(
                  JSON.stringify({
                    event: "navigate_back",
                  })
                );
              } else {
                navigate("/", { replace: true });
              }
            }
          }

          setUser((user) => ({
            ...user,
            ...data.result,
          }));
        }
      },
    }
  );

  const { mutate: appsflyerSendEvent } = useMutation({
    mutationFn: (eventName: string) => appsflyerApi.sendEvent(eventName),
    onSuccess: () => {},
  });

  useEffect(() => {
    if (parsedMessageFromApp?.event === "payment") {
      const status = parsedMessageFromApp.params.status;
      if (status) {
        setIsLoading(false);
        if (status === "success" && !isDone) {
          queryClient.invalidateQueries([USER_DOMAIN, USER_DETAIL_ENDPOINT]);
          sendMessage(
            JSON.stringify({
              event: "navigate_tab",
              params: {
                tabName: "코칭",
              },
            })
          );
          setIsDone(true);
        }
      }
    }
  }, [parsedMessageFromApp]);

  const buttonText = "프로그램 시작하기";

  const requestPayment = () => {
    if (paymentOrderState) {
      if (paymentOrderState?.paymentProvider === "in_app") {
        if (isInappWebview) {
          if (paymentOrderState?.firstMonthOfferLinkIos) {
            sendMessage(
              JSON.stringify({
                event: "open_link",
                params: {
                  url: paymentOrderState.firstMonthOfferLinkIos,
                },
              })
            );
          } else {
            setIsLoading(true);

            sendMessage(
              JSON.stringify({
                event: "payment",
                params: {
                  productId: paymentOrderState?.productId,
                  offer: paymentOrderState?.offer,
                },
              })
            );
          }
        }
      } else if (paymentOrderState?.paymentProvider === "toss_payments") {
        if (paymentOrderState?.isStoreReviewer) {
          setIsLoading(true);

          confirmStoreReviewerPayments(
            { orderId: paymentOrderState?.orderId!! },
            {
              onSuccess: () => {
                if (isInappWebview) {
                  setIsLoading(false);

                  setDisabled(true);

                  sendMessage(
                    JSON.stringify({
                      event: "navigate_back",
                    })
                  );
                } else {
                  // 웹앱이면 메인화면으로
                  navigate("/", { replace: true });
                }
              },
            }
          );

          return;
        }

        if (isInappWebview) {
          setIsLoading(true);

          if (
            user?.isInsider !== true &&
            compareVersions(appVersion, "1.1.17") >= 0 &&
            paymentOrderState.isFirst
          ) {
            if (user?.segmentType) {
              sendMessage(
                JSON.stringify({
                  event: "send_fb_event",
                  params: {
                    eventName: `btn_click_payment_type_${user?.segmentType}`,
                  },
                })
              );
            }
            sendMessage(
              JSON.stringify({
                event: "send_fb_event",
                params: {
                  eventName: "btn_click_payment",
                },
              })
            );
          }

          sendMessage(
            JSON.stringify({
              event: "request_toss_payments",
              params: {
                url: `${window.location.origin}${PATH.tossPayments}?plan=${selectedPlan}`,
              },
            })
          );

          setTimeout(() => {
            setIsLoading(false);
          }, 5000);
        } else {
          ReactPixel.track("StartTrial", {
            value: "0.00",
            currency: "KRW",
            predicted_ltv: "0.00",
          });

          navigate(`/toss-payments?plan=${selectedPlan}`);
        }
      }

      if (paymentOrderState.isFirst) {
        appsflyerSendEvent("결제버튼클릭");
      }

      setMixPanel();
    }
  };

  const { mutate: reassignCoach } = useMutation(() => userApi.reassignCoach(), {
    onSuccess: () => {},
  });

  return user?.isCoachChanged ? (
    <ButtonWithModal
      size={"lg"}
      color={"primary"}
      buttonFullWidth
      buttonText={buttonText ?? ""}
      disabled={disabled}
      loading={isLoading}
      title={"담당 코치가 변경되었어요"}
      subtitle={`${user?.assignedCoachName} 선생님은 더이상 디스턴싱과 함께하지 않게 되었어요.`}
      textAlign="center"
      actionText="새 코치 만나기"
      action={() => {
        reassignCoach();
        requestPayment();
      }}
    />
  ) : (
    <Button
      loading={isLoading}
      disabled={disabled}
      onClick={requestPayment}
      size={"lg"}
      color={"primary"}
      sx={{ width: "100%", fontWeight: 700 }}
    >
      {buttonText}
    </Button>
  );
}
